import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  UploadIcon,
  PhoneIcon,
  MailIcon,
  CalendarIcon,
} from "@heroicons/react/outline";
import {
  addTeamMemberThunk,
  updateTeamMemberThunk,
  deleteTeamMemberThunk,
} from "../../../../store/thunks/branchDisplayThunks";

const INITIAL_FORM = {
  name: "",
  designation: "",
  phone: "",
  email: "",
  bio: "",
  specialization: "",
  experience: "",
  languages: [],
  isPublic: true,
  isAvailableForAppointment: false,
};

const TeamSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const { teamMembers, uploading, loadingSection } = useSelector(
    (state) => state.branchDisplay
  );
  const fileInputRef = useRef(null);

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [selectedFile, setSelectedFile] = useState(null);
  const [languageInput, setLanguageInput] = useState("");

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddLanguage = () => {
    if (languageInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        languages: [...prev.languages, languageInput.trim()],
      }));
      setLanguageInput("");
    }
  };

  const handleRemoveLanguage = (index) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.filter((_, i) => i !== index),
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleAdd = async () => {
    const formDataObj = new FormData();
    
    if (selectedFile) {
      formDataObj.append("photo", selectedFile);
    }
    
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "languages") {
        formDataObj.append(key, JSON.stringify(value));
      } else {
        formDataObj.append(key, value);
      }
    });

    await dispatch(addTeamMemberThunk({ branchCode, formData: formDataObj }));
    handleCancel();
  };

  const handleEdit = (member) => {
    setEditingId(member._id);
    setFormData({
      name: member.name || "",
      designation: member.designation || "",
      phone: member.phone || "",
      email: member.email || "",
      bio: member.bio || "",
      specialization: member.specialization || "",
      experience: member.experience || "",
      languages: member.languages || [],
      isPublic: member.isPublic ?? true,
      isAvailableForAppointment: member.isAvailableForAppointment ?? false,
    });
  };

  const handleUpdate = async () => {
    await dispatch(
      updateTeamMemberThunk({
        branchCode,
        memberId: editingId,
        data: formData,
      })
    );
    handleCancel();
  };

  const handleDelete = async (memberId) => {
    if (window.confirm("Are you sure you want to remove this team member?")) {
      await dispatch(deleteTeamMemberThunk({ branchCode, memberId }));
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData(INITIAL_FORM);
    setSelectedFile(null);
  };

  const renderForm = () => (
    <div className="bg-white border rounded-lg p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="Full name"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Designation *</label>
          <input
            type="text"
            value={formData.designation}
            onChange={(e) => handleInputChange("designation", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="e.g., Store Manager"
            required
          />
        </div>
      </div>

      {/* Photo Upload (only for new members) */}
      {isAdding && (
        <div>
          <label className="block text-sm text-gray-600 mb-1">Photo</label>
          <div className="flex items-center gap-4">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleFileSelect}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <UploadIcon className="h-4 w-4" />
              {selectedFile ? selectedFile.name : "Select Photo"}
            </button>
            {selectedFile && (
              <button
                type="button"
                onClick={() => setSelectedFile(null)}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="+91 9876543210"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="email@example.com"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Specialization</label>
          <input
            type="text"
            value={formData.specialization}
            onChange={(e) => handleInputChange("specialization", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="e.g., Diamond Jewelry"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Experience</label>
          <input
            type="text"
            value={formData.experience}
            onChange={(e) => handleInputChange("experience", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="e.g., 10+ years"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-600 mb-1">Bio</label>
        <textarea
          value={formData.bio}
          onChange={(e) => handleInputChange("bio", e.target.value)}
          className="w-full px-3 py-2 border rounded-lg"
          rows="2"
          placeholder="Brief introduction..."
        />
      </div>

      {/* Languages */}
      <div>
        <label className="block text-sm text-gray-600 mb-1">Languages</label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={languageInput}
            onChange={(e) => setLanguageInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddLanguage())}
            className="flex-1 px-3 py-2 border rounded-lg"
            placeholder="Add language"
          />
          <button
            type="button"
            onClick={handleAddLanguage}
            className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.languages.map((lang, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1"
            >
              {lang}
              <button
                type="button"
                onClick={() => handleRemoveLanguage(index)}
                className="text-blue-500 hover:text-blue-700"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isPublic}
            onChange={(e) => handleInputChange("isPublic", e.target.checked)}
            className="h-4 w-4 rounded"
          />
          <span className="text-sm">Show on App/Website</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isAvailableForAppointment}
            onChange={(e) => handleInputChange("isAvailableForAppointment", e.target.checked)}
            className="h-4 w-4 rounded"
          />
          <span className="text-sm">Available for Appointments</span>
        </label>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          onClick={editingId ? handleUpdate : handleAdd}
          disabled={
            (loadingSection === "team" || uploading) ||
            !formData.name ||
            !formData.designation
          }
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
        >
          {(loadingSection === "team" || uploading) ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <CheckCircleIcon className="h-4 w-4" />
          )}
          {editingId ? "Update Member" : "Add Member"}
        </button>
        <button
          onClick={handleCancel}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Add Button */}
      {isRootAdmin && !isAdding && !editingId && (
        <button
          onClick={() => setIsAdding(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Add Team Member
        </button>
      )}

      {/* Add/Edit Form */}
      {(isAdding || editingId) && renderForm()}

      {/* Team Members List */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4 flex items-center">
          <UserGroupIcon className="h-4 w-4 mr-2" />
          Team Members ({teamMembers.length})
        </h4>

        {teamMembers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <UserGroupIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No team members added yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamMembers.map((member) => (
              <div
                key={member._id}
                className={`bg-white border rounded-lg p-4 ${
                  !member.isPublic ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-16 h-16 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                    {member.photoUrl ? (
                      <img
                        src={member.photoUrl}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-2xl font-medium">
                        {member.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h5 className="font-medium truncate">{member.name}</h5>
                      {!member.isPublic && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                          Hidden
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{member.designation}</p>
                    
                    {member.specialization && (
                      <p className="text-xs text-blue-600 mt-1">{member.specialization}</p>
                    )}

                    <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                      {member.phone && (
                        <span className="flex items-center gap-1">
                          <PhoneIcon className="h-3 w-3" />
                          {member.phone}
                        </span>
                      )}
                      {member.isAvailableForAppointment && (
                        <span className="flex items-center gap-1 text-green-600">
                          <CalendarIcon className="h-3 w-3" />
                          Appointments
                        </span>
                      )}
                    </div>

                    {member.languages?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {member.languages.map((lang, idx) => (
                          <span
                            key={idx}
                            className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {isRootAdmin && !editingId && (
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleEdit(member)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(member._id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamSection;
